<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  listChannelPosts,
  createChannelPost,
  updateChannelPost,
  deleteChannelPost,
  uploadChannelCover,
  listChannelSignups,
  adImageAbsUrl,
  type ChannelPostRow,
  type ChannelType,
  type ChannelPostPayload,
  type ChannelSignupRow,
} from '../api';

const CHANNELS: { value: ChannelType; label: string }[] = [
  { value: 'HEADLINE', label: '头条' },
  { value: 'TRAINING_CAMP', label: '训练营' },
];
const channelLabel = (v: string) => CHANNELS.find((c) => c.value === v)?.label ?? v;

const filter = ref<ChannelType | ''>('');
const loading = ref(false);
const list = ref<ChannelPostRow[]>([]);

function normalize(e: unknown): string {
  const err = e as { message?: string };
  return err.message ?? '操作失败';
}

async function reload() {
  loading.value = true;
  try {
    const r = await listChannelPosts(filter.value || undefined);
    list.value = r.items;
  } catch (e) {
    ElMessage.error(normalize(e));
  } finally {
    loading.value = false;
  }
}

// —— 新增/编辑弹窗 ——
const dialogVisible = ref(false);
const editingId = ref<string | null>(null);
const uploading = ref(false);
const saving = ref(false);
const form = reactive<ChannelPostPayload>({
  channel: 'HEADLINE',
  title: '',
  body: '',
  coverUrl: '',
  coverKey: '',
  status: 'DRAFT',
  signupEnabled: false,
  sort: 0,
});

function resetForm(channel: ChannelType = 'HEADLINE') {
  editingId.value = null;
  Object.assign(form, {
    channel,
    title: '',
    body: '',
    coverUrl: '',
    coverKey: '',
    status: 'DRAFT',
    signupEnabled: false,
    sort: 0,
  });
}

function openCreate() {
  resetForm((filter.value || 'HEADLINE') as ChannelType);
  dialogVisible.value = true;
}

function openEdit(row: ChannelPostRow) {
  editingId.value = row.id;
  Object.assign(form, {
    channel: row.channel,
    title: row.title,
    body: row.body,
    coverUrl: row.coverUrl ?? '',
    coverKey: row.coverKey ?? '',
    status: row.status,
    signupEnabled: row.signupEnabled,
    sort: row.sort,
  });
  dialogVisible.value = true;
}

async function onPickCover(e: Event) {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  uploading.value = true;
  try {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const r = await uploadChannelCover(dataUrl, file.type, file.name);
    form.coverUrl = r.url;
    form.coverKey = r.key;
    ElMessage.success('封面已上传');
  } catch (err) {
    ElMessage.error(normalize(err));
  } finally {
    uploading.value = false;
    input.value = '';
  }
}

async function onSave() {
  if (!form.title.trim()) return ElMessage.warning('请填写标题');
  if (!form.body.trim()) return ElMessage.warning('请填写正文');
  saving.value = true;
  try {
    if (editingId.value) {
      await updateChannelPost(editingId.value, form);
      ElMessage.success('已更新');
    } else {
      await createChannelPost(form);
      ElMessage.success('已创建');
    }
    dialogVisible.value = false;
    await reload();
  } catch (e) {
    ElMessage.error(normalize(e));
  } finally {
    saving.value = false;
  }
}

async function onDelete(row: ChannelPostRow) {
  try {
    await ElMessageBox.confirm(`确认删除「${row.title}」?`, '删除', { type: 'warning' });
    await deleteChannelPost(row.id);
    ElMessage.success('已删除');
    await reload();
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(normalize(e));
  }
}

// —— 报名名单 ——
const signupVisible = ref(false);
const signupLoading = ref(false);
const signups = ref<ChannelSignupRow[]>([]);
const signupTitle = ref('');

async function openSignups(row: ChannelPostRow) {
  signupTitle.value = row.title;
  signupVisible.value = true;
  signupLoading.value = true;
  signups.value = [];
  try {
    const r = await listChannelSignups(row.id);
    signups.value = r.items;
  } catch (e) {
    ElMessage.error(normalize(e));
  } finally {
    signupLoading.value = false;
  }
}

onMounted(reload);
</script>

<template>
  <div>
    <div class="bar">
      <el-select v-model="filter" placeholder="全部频道" clearable style="width: 160px" @change="reload">
        <el-option v-for="c in CHANNELS" :key="c.value" :label="c.label" :value="c.value" />
      </el-select>
      <div>
        <el-button @click="reload" :loading="loading">刷新</el-button>
        <el-button type="primary" @click="openCreate">发布内容</el-button>
      </div>
    </div>

    <el-table :data="list" v-loading="loading" style="width: 100%">
      <el-table-column label="频道" width="90">
        <template #default="{ row }">
          <el-tag :type="row.channel === 'HEADLINE' ? 'danger' : 'warning'" size="small">
            {{ channelLabel(row.channel) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="title" label="标题" width="180" show-overflow-tooltip />
      <el-table-column prop="body" label="正文" show-overflow-tooltip />
      <el-table-column label="状态" width="90">
        <template #default="{ row }">
          <el-tag :type="row.status === 'PUBLISHED' ? 'success' : 'info'" size="small">
            {{ row.status === 'PUBLISHED' ? '已发布' : '草稿' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="报名" width="120">
        <template #default="{ row }">
          <template v-if="row.channel === 'TRAINING_CAMP' && row.signupEnabled">
            <el-button size="small" link type="primary" @click="openSignups(row)">
              名单({{ row.signupCount }})
            </el-button>
          </template>
          <span v-else class="muted">—</span>
        </template>
      </el-table-column>
      <el-table-column prop="sort" label="排序" width="70" />
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <el-button size="small" @click="openEdit(row)">编辑</el-button>
          <el-button size="small" type="danger" @click="onDelete(row)">删除</el-button>
        </template>
      </el-table-column>
      <template #empty>暂无内容</template>
    </el-table>

    <!-- 发布/编辑弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="editingId ? '编辑内容' : '发布内容'"
      width="600px"
    >
      <el-form label-width="88px">
        <el-form-item label="频道">
          <el-radio-group v-model="form.channel" :disabled="!!editingId">
            <el-radio v-for="c in CHANNELS" :key="c.value" :value="c.value">{{ c.label }}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="标题">
          <el-input v-model="form.title" maxlength="128" show-word-limit />
        </el-form-item>
        <el-form-item label="正文">
          <el-input v-model="form.body" type="textarea" :rows="6" />
        </el-form-item>
        <el-form-item label="封面">
          <div>
            <el-image
              v-if="form.coverUrl"
              :src="adImageAbsUrl(form.coverUrl)"
              fit="cover"
              style="width: 120px; height: 68px; border-radius: 6px; display: block; margin-bottom: 8px"
            />
            <input type="file" accept="image/jpeg,image/png,image/webp" :disabled="uploading" @change="onPickCover" />
          </div>
        </el-form-item>
        <el-form-item v-if="form.channel === 'TRAINING_CAMP'" label="开放报名">
          <el-switch v-model="form.signupEnabled" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio value="DRAFT">草稿</el-radio>
            <el-radio value="PUBLISHED">发布</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sort" :min="0" />
          <span class="muted" style="margin-left: 8px">小值在前</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="onSave">保存</el-button>
      </template>
    </el-dialog>

    <!-- 报名名单 -->
    <el-dialog v-model="signupVisible" :title="`报名名单 · ${signupTitle}`" width="560px">
      <el-table :data="signups" v-loading="signupLoading" max-height="420">
        <el-table-column prop="name" label="姓名" width="120" />
        <el-table-column prop="mobile" label="手机" width="140" />
        <el-table-column prop="note" label="备注" show-overflow-tooltip />
        <el-table-column prop="createdAt" label="报名时间" width="180" />
        <template #empty>暂无报名</template>
      </el-table>
    </el-dialog>
  </div>
</template>

<style scoped>
.bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.muted {
  color: #999;
  font-size: 13px;
}
</style>
